import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { API_DOMAIN, API_ROUTES } from '../../Reusable_Components/Config/apiConfig';
import * as listCache from '../../../services/listCache';
import { fmtDate } from '../../../utils/fmt';

// ── Auth helper ───────────────────────────────────────────────────────────────

const checkSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return !!user.is_superadmin;
  } catch { return false; }
};

// ── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, accent }) => (
  <div className="stat-card">
    <div className="stat-card-icon" style={{ background: accent.bg, color: accent.fg }}>
      {icon}
    </div>
    <div className="stat-card-info">
      {value === null ? (
        <div className="stat-card-loading" />
      ) : (
        <div className="stat-card-value">{value !== undefined ? value.toLocaleString() : '—'}</div>
      )}
      <div className="stat-card-label">{label}</div>
    </div>
  </div>
);

// ── SVG Icons ─────────────────────────────────────────────────────────────────

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const LeadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const TargetIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const DocIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const getGreeting = (name) => {
  const h = new Date().getHours();
  const base = h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
  const displayName = name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
  return displayName ? `${base}, ${displayName}` : base;
};


const getLeadName = (lead) => {
  if (lead.full_name) return lead.full_name;
  const first = lead.first_name || '';
  const last = lead.last_name || '';
  if (first || last) return `${first} ${last}`.trim();
  if (lead.name) return lead.name;
  if (lead.title) return lead.title;
  return `Lead #${lead.id}`;
};

// Extract a record count from raw API response data (no axios wrapper)
const countRaw = (d) => {
  if (!d) return null;
  if (Array.isArray(d)) return d.length;
  if (typeof d.count === 'number') return d.count;
  const inner = d.data ?? d;
  if (Array.isArray(inner)) return inner.length;
  if (typeof inner?.count === 'number') return inner.count;
  if (Array.isArray(inner?.results)) return inner.results.length;
  return null;
};

const extractLeads = (d) => {
  if (!d) return [];
  const results = d.results ?? (Array.isArray(d) ? d : d?.data?.results ?? []);
  return results.slice(0, 6);
};

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '8px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#475569' }}>{payload[0].value.toLocaleString()} records</div>
    </div>
  );
};

const CHART_COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#e87860', '#8b5cf6'];

const ACCENTS = {
  blue:   { bg: '#dbeafe', fg: '#1d4ed8' },
  amber:  { bg: '#fef3c7', fg: '#d97706' },
  green:  { bg: '#dcfce7', fg: '#16a34a' },
  orange: { bg: '#fff0ed', fg: '#e87860' },
};

// ── Quick actions (superadmin sidebar) ────────────────────────────────────────

const QUICK_ACTIONS = [
  { label: 'New Lead',        to: '/lead/input',           bg: '#fef9c3', icon: '⚡' },
  { label: 'New Contact',     to: '/contact/input',        bg: '#dbeafe', icon: '👤' },
  { label: 'New Opportunity', to: '/opportunity/input',    bg: '#dcfce7', icon: '🎯' },
  { label: 'New Quote',       to: '/quote/input',          bg: '#fff0ed', icon: '📄' },
  { label: 'New Customer',    to: '/customer/stage/input', bg: '#f3e8ff', icon: '🏢' },
];

// ── Strategic quick actions (non-superadmin) ──────────────────────────────────

const STRATEGIC_ACTIONS = [
  {
    icon: '⚡', label: 'Capture a Lead',
    desc: 'Log a new prospect before the moment passes',
    to: '/lead/input', color: '#f59e0b', bg: '#fffbeb', tag: 'Most used',
  },
  {
    icon: '👤', label: 'Add a Contact',
    desc: "Store a person's details and full history in one place",
    to: '/contact/input', color: '#3b82f6', bg: '#dbeafe',
  },
  {
    icon: '🎯', label: 'Open a Deal',
    desc: 'Turn a qualified lead into a tracked opportunity',
    to: '/opportunity/input', color: '#22c55e', bg: '#dcfce7', tag: 'Pipeline',
  },
  {
    icon: '📄', label: 'Write a Quote',
    desc: 'Draft a proposal and move the deal forward',
    to: '/quote/input', color: '#e87860', bg: '#fff0ed',
  },
  {
    icon: '🏢', label: 'Onboard a Customer',
    desc: 'Add a won account and start building the relationship',
    to: '/customer/stage/input', color: '#8b5cf6', bg: '#f3e8ff', tag: 'Revenue',
  },
  {
    icon: '✅', label: 'Schedule a Task',
    desc: 'Log a call, meeting, or follow-up so nothing slips',
    to: '/task/input', color: '#0ea5e9', bg: '#e0f2fe',
  },
];

const PIPELINE_SHORTCUTS = [
  { icon: '📋', label: 'Leads',         to: '/lead/list',        color: '#f59e0b' },
  { icon: '👥', label: 'Contacts',      to: '/contact/list',     color: '#3b82f6' },
  { icon: '💼', label: 'Opportunities', to: '/opportunity/list', color: '#22c55e' },
  { icon: '📑', label: 'Quotes',        to: '/quote/list',       color: '#e87860' },
  { icon: '🏢', label: 'Customers',     to: '/customer/list',    color: '#8b5cf6' },
  { icon: '✔',  label: 'Tasks',         to: '/tasks/list',       color: '#0ea5e9' },
];

// ── ActionCard sub-component ──────────────────────────────────────────────────

const ActionCard = ({ icon, label, desc, to, color, bg, tag }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column', gap: 12,
        padding: '20px', borderRadius: 12, textDecoration: 'none',
        background: 'var(--card-bg)',
        borderTop: `3px solid ${color}`,
        borderRight: `1px solid ${hovered ? color + '55' : 'var(--border)'}`,
        borderBottom: `1px solid ${hovered ? color + '55' : 'var(--border)'}`,
        borderLeft: `1px solid ${hovered ? color + '55' : 'var(--border)'}`,
        boxShadow: hovered ? `0 6px 24px ${color}20` : 'none',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'all 0.16s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: bg, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 21,
        }}>
          {icon}
        </div>
        {tag && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
            background: color + '18', color, border: `1px solid ${color}30`,
          }}>
            {tag}
          </span>
        )}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', marginBottom: 5 }}>
          {label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          {desc}
        </div>
      </div>
      <div style={{
        fontSize: 12, color, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 5,
        opacity: hovered ? 1 : 0.7, transition: 'opacity 0.15s',
      }}>
        Get started <span style={{ fontSize: 14 }}>→</span>
      </div>
    </Link>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const superAdmin = checkSuperAdmin();
  const [stats, setStats] = useState({
    customers: null, leads: null, opportunities: null, quotes: null, contacts: null,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [now, setNow] = useState(new Date());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fetchErrors, setFetchErrors] = useState([]);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const ENTRIES = [
      ['customers',     API_DOMAIN + API_ROUTES.customer],
      ['leads',         API_DOMAIN + API_ROUTES.lead],
      ['opportunities', API_DOMAIN + API_ROUTES.opportunity],
      ['quotes',        API_DOMAIN + API_ROUTES.quote],
      ['contacts',      API_DOMAIN + API_ROUTES.contact],
    ];

    // ── Step 1: Render cached data immediately (zero wait) ────────
    const statsNow = {};
    for (const [key, url] of ENTRIES) {
      const cached = listCache.get(url);
      if (!cached) continue;
      statsNow[key] = countRaw(cached.data);
      if (key === 'leads') {
        setRecentLeads(extractLeads(cached.data));
        setLoadingLeads(false);
      }
    }
    if (Object.keys(statsNow).length > 0) setStats(prev => ({ ...prev, ...statsNow }));

    // ── Step 2: Background-revalidate stale or missing entries ────
    const toFetch = ENTRIES.filter(([, url]) => {
      const entry = listCache.get(url);
      return !entry || entry.isStale;
    });
    if (toFetch.length === 0) return;

    Promise.allSettled(
      toFetch.map(([, url]) =>
        listCache.fetchDeduped(url, {})
          .then(data => { listCache.set(url, data); return data; })
      )
    ).then(results => {
      const updates = {};
      const errors = [];
      results.forEach((res, i) => {
        const [key] = toFetch[i];
        if (res.status !== 'fulfilled') {
          errors.push(key);
          return;
        }
        updates[key] = countRaw(res.value);
        if (key === 'leads') {
          setRecentLeads(extractLeads(res.value));
          setLoadingLeads(false);
        }
      });
      if (Object.keys(updates).length > 0) setStats(prev => ({ ...prev, ...updates }));
      setLastUpdated(new Date());
      if (errors.length > 0) {
        setFetchErrors(errors);
      }
    });
  }, []);

  const chartData = [
    { name: 'Contacts',      value: stats.contacts      ?? 0 },
    { name: 'Leads',         value: stats.leads         ?? 0 },
    { name: 'Opportunities', value: stats.opportunities ?? 0 },
    { name: 'Quotes',        value: stats.quotes        ?? 0 },
    { name: 'Customers',     value: stats.customers     ?? 0 },
  ];

  const today = now.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const currentTime = now.toLocaleTimeString('en-GB');
  const username = localStorage.getItem('username');

  return (
    <div>
      {/* ── Error banner if any data fetch failed ── */}
      {fetchErrors.length > 0 && (
        <div style={{
          background: '#fff0ed', border: '1px solid #e87860', borderRadius: 8,
          padding: '12px 16px', marginBottom: 20, color: '#c05442', fontSize: 13,
        }}>
          Failed to load data for: {fetchErrors.join(', ')}. Some statistics may be outdated.
        </div>
      )}

      {/* ── Header ── */}
      <div className="dashboard-header">
        <div>
          <div className="dashboard-greeting">{getGreeting(username)}</div>
          <div className="dashboard-date">{today} &nbsp;•&nbsp; {currentTime}</div>
          {lastUpdated && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Last updated: {lastUpdated.toLocaleTimeString('en-GB')}
            </div>
          )}
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="stat-cards">
        <StatCard icon={<UsersIcon />}  label="Customers"     value={stats.customers}     accent={ACCENTS.blue} />
        <StatCard icon={<LeadIcon />}   label="Leads"         value={stats.leads}         accent={ACCENTS.amber} />
        <StatCard icon={<TargetIcon />} label="Opportunities" value={stats.opportunities} accent={ACCENTS.green} />
        <StatCard icon={<DocIcon />}    label="Quotes"        value={stats.quotes}        accent={ACCENTS.orange} />
      </div>

      {/* ── Strategic Quick Actions — non-superadmin only ── */}
      {!superAdmin && (
        <div style={{ marginBottom: 24 }}>
          {/* Section header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>
                Quick Actions
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Your most-used sales tools, one click away
              </div>
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-muted)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 7, padding: '5px 12px',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span>More commands:</span>
              <kbd style={{
                fontSize: 11, padding: '1px 6px', borderRadius: 4,
                background: 'var(--border)', border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: 'inherit',
              }}>⌘K</kbd>
            </div>
          </div>

          {/* 3-column action cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {STRATEGIC_ACTIONS.map(action => (
              <ActionCard key={action.to} {...action} />
            ))}
          </div>

          {/* Pipeline shortcut strip */}
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            {PIPELINE_SHORTCUTS.map(({ icon, label, to, color }) => (
              <Link
                key={to}
                to={to}
                className="pipeline-shortcut"
                style={{
                  flex: '1 1 0', minWidth: 90,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '9px 12px', borderRadius: 8, textDecoration: 'none',
                  background: 'var(--card-bg)', border: '1px solid var(--border)',
                  fontSize: 12, fontWeight: 600, color: 'var(--text-sub)',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = color;
                  e.currentTarget.style.borderColor = color + '55';
                  e.currentTarget.style.background = color + '10';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = 'var(--text-sub)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.background = 'var(--card-bg)';
                }}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Main Grid ── */}
      <div
        className="dashboard-grid"
        style={!superAdmin ? { gridTemplateColumns: '1fr' } : {}}
      >
        {/* Recent Leads */}
        <div className="recent-table">
          <div className="recent-table-header">
            <span className="recent-table-title">Recent Leads</span>
            <Link to="/lead/list" className="recent-table-link">View all →</Link>
          </div>
          {!loadingLeads && recentLeads.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px 24px' }}>
              <div className="empty-state-icon">⚡</div>
              <div className="empty-state-text">No leads yet — create your first one</div>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLeads && recentLeads.length === 0
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          {[90, 60, 70, 40].map((w, j) => (
                            <td key={j}>
                              <div className="skeleton" style={{ height: 13, width: `${w}%`, borderRadius: 4 }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : recentLeads.map((lead) => (
                        <tr key={lead.id}>
                          <td style={{ fontWeight: 500 }}>{getLeadName(lead)}</td>
                          <td>
                            {lead.status
                              ? <span className="badge badge-blue">{lead.status}</span>
                              : '—'}
                          </td>
                          <td>{fmtDate(lead.created_at)}</td>
                          <td className="actions-cell">
                            <Link to={`/lead/details/${btoa(lead.id)}`} className="btn btn-ghost btn-sm">
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Simple Quick Actions sidebar — superadmin only */}
        {superAdmin && (
          <div className="quick-actions">
            <div className="quick-actions-header">Quick Actions</div>
            {QUICK_ACTIONS.map(({ label, to, bg, icon }) => (
              <Link key={to} to={to} className="quick-action-btn">
                <span className="quick-action-dot" style={{ background: bg }}>{icon}</span>
                <span style={{ flex: 1 }}>{label}</span>
                <span style={{ color: 'var(--text-muted)' }}><PlusIcon /></span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* ── Pipeline Chart ── */}
      <div className="chart-card">
        <div className="chart-card-header">
          <span className="chart-card-title">Pipeline Overview</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total records per module</span>
        </div>
        <div className="chart-card-body">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 0 }} barSize={44}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
